import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Eye,
  Hash,
  Info,
  Pencil,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  XCircle,
  Zap,
} from "lucide-react";
import {
  useAgentBaseline,
  useAgentDriftEvents,
  useAgentRules,
  useUpdateAgentRule,
  useUpdateDriftEventStatus,
  useRecomputeBaseline,
} from "@/hooks/useDriftDetection";
import { safeFormatDistanceToNow } from "@/utils/dateUtils";
import type { AgentBaseline, DriftEvent, DriftEventStatus, DriftRule, DriftSeverity } from "@/types/drift";

interface DriftTabProps {
  agentId: number;
}

// ── Health Score ──────────────────────────────────────────────────────

function computeHealthScore(events: DriftEvent[] | undefined): number {
  if (!events || events.length === 0) return 100;
  const open = events.filter((e) => e.status === "OPEN");
  if (open.length === 0) return 100;

  let penalty = 0;
  for (const e of open) {
    if (e.severity === "CRITICAL") penalty += 30;
    else if (e.severity === "HIGH") penalty += 15;
    else if (e.severity === "MEDIUM") penalty += 7;
    else penalty += 3;
  }
  return Math.max(0, 100 - penalty);
}

function healthLabel(score: number): string {
  if (score >= 80) return "Healthy";
  if (score >= 60) return "Warning";
  if (score >= 40) return "Degraded";
  return "Critical";
}

// ── Badge Helpers ────────────────────────────────────────────────────

const SEVERITY_COLORS: Record<DriftSeverity, string> = {
  CRITICAL: "bg-red-100 text-red-800 border-red-200",
  HIGH: "bg-orange-100 text-orange-800 border-orange-200",
  MEDIUM: "bg-yellow-100 text-yellow-800 border-yellow-200",
  LOW: "bg-gray-100 text-gray-600 border-gray-200",
  INFO: "bg-blue-100 text-blue-800 border-blue-200",
};

const STATUS_STYLES: Record<DriftEventStatus, { className: string; icon: typeof CheckCircle2 }> = {
  OPEN: { className: "bg-red-100 text-red-800 border-red-200", icon: ShieldAlert },
  ACKNOWLEDGED: { className: "bg-blue-100 text-blue-800 border-blue-200", icon: Eye },
  FALSE_POSITIVE: { className: "bg-gray-100 text-gray-600 border-gray-200", icon: XCircle },
  RESOLVED: { className: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2 },
};

const DRIFT_TYPE_LABELS: Record<string, string> = {
  system_prompt_changed: "Prompt Changed",
  token_spike: "Token Spike",
  risk_score_trend: "Risk Trend",
  off_hours_activity: "Off-Hours",
  tool_usage_changed: "Tool Changed",
  volume_spike: "Volume Spike",
  volume_drop: "Volume Drop",
  model_changed: "Model Changed",
  scanner_pattern_changed: "Scanner Pattern",
};

const MIN_RELIABLE_SAMPLES = 50;

// ── Guidance Banner ──────────────────────────────────────────────────

function BaselineGuidance({
  baseline,
  onCompute,
  isComputing,
}: {
  baseline: AgentBaseline | undefined;
  onCompute: () => void;
  isComputing: boolean;
}) {
  if (isComputing) {
    return (
      <Card className="rounded-[15px] border-blue-200 bg-blue-50/50 shadow-none">
        <CardContent className="py-4 flex items-start gap-3">
          <RefreshCw className="h-4 w-4 text-blue-500 mt-0.5 shrink-0 animate-spin" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">Computing baseline...</p>
            <p className="text-xs text-blue-700 mt-1">
              Analyzing your agent's recent activity to establish a behavioral baseline.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!baseline) {
    return (
      <Card className="rounded-[15px] border-blue-200 bg-blue-50/50 shadow-none">
        <CardContent className="py-4 flex items-start gap-3">
          <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">Drift detection needs a baseline to get started</p>
            <p className="text-xs text-blue-700 mt-1">
              A baseline captures your agent's normal behavior — token usage, request patterns,
              risk scores, and active hours. Once established, we'll automatically flag any
              significant changes so you can catch issues early.
            </p>
            <p className="text-xs text-blue-600 mt-1.5">
              We recommend at least {MIN_RELIABLE_SAMPLES} requests before computing a baseline for reliable results.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 h-8 text-xs border-blue-200 text-blue-700 hover:bg-blue-100"
            onClick={onCompute}
            disabled={isComputing}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Compute Baseline
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (baseline.status === "INSUFFICIENT_DATA") {
    return (
      <Card className="rounded-[15px] border-amber-200 bg-amber-50/50 shadow-none">
        <CardContent className="py-4 flex items-start gap-3">
          <Info className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">Not enough data for a reliable baseline yet</p>
            <p className="text-xs text-amber-700 mt-1">
              We found {baseline.sample_count.toLocaleString()} request{baseline.sample_count !== 1 ? "s" : ""} in the
              last {baseline.baseline_window_days} days. We need at least {MIN_RELIABLE_SAMPLES} to
              establish a meaningful baseline. This will improve automatically as your agent processes more requests.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (baseline.status === "ACTIVE" && baseline.sample_count < MIN_RELIABLE_SAMPLES) {
    return (
      <Card className="rounded-[15px] border-amber-200 bg-amber-50/50 shadow-none">
        <CardContent className="py-4 flex items-start gap-3">
          <Info className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">Baseline accuracy is limited</p>
            <p className="text-xs text-amber-700 mt-1">
              The current baseline is built from {baseline.sample_count.toLocaleString()} request{baseline.sample_count !== 1 ? "s" : ""}.
              With fewer than {MIN_RELIABLE_SAMPLES} samples, drift alerts may be less precise.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (baseline.status === "STALE") {
    return (
      <Card className="rounded-[15px] border-amber-200 bg-amber-50/50 shadow-none">
        <CardContent className="py-4 flex items-start gap-3">
          <Info className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">Baseline is out of date</p>
            <p className="text-xs text-amber-700 mt-1">
              The baseline was last computed {safeFormatDistanceToNow(baseline.computed_at)} ago
              and has expired. Recompute it to get accurate drift detection based on recent behavior.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 h-8 text-xs border-amber-200 text-amber-700 hover:bg-amber-100"
            onClick={onCompute}
            disabled={isComputing}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Recompute
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
}

// ── Rule Row ─────────────────────────────────────────────────────────

function RuleRow({
  rule,
  onUpdate,
  isUpdating,
  pendingRuleId,
  pendingEnabled,
}: {
  rule: DriftRule;
  onUpdate: (ruleId: string, update: { enabled?: boolean; threshold?: number; severity?: string }) => void;
  isUpdating: boolean;
  pendingRuleId: string | undefined;
  pendingEnabled: boolean | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [threshold, setThreshold] = useState(rule.threshold?.toString() ?? "");
  const [severity, setSeverity] = useState(rule.current_severity);

  // Derived state: show optimistic value while mutation is pending for THIS rule
  const isEnabled = (isUpdating && pendingRuleId === rule.rule_id && pendingEnabled !== undefined)
    ? pendingEnabled
    : rule.enabled;

  const handleSave = () => {
    const update: { enabled?: boolean; threshold?: number; severity?: string } = {};
    if (rule.threshold_label && threshold) {
      const num = parseFloat(threshold);
      if (!isNaN(num) && num > 0) update.threshold = num;
    }
    if (severity !== rule.current_severity) update.severity = severity;
    if (Object.keys(update).length > 0) onUpdate(rule.rule_id, update);
    setEditing(false);
  };

  if (!rule.available) {
    return (
      <div className="flex items-center gap-3 py-2 border-b border-dashed border-gray-100 last:border-b-0">
        <div className="w-3 flex justify-center">
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
        </div>
        <span className="text-sm text-muted-foreground flex-1">{rule.name}</span>
        <span className="text-[10px] text-muted-foreground max-w-[300px] truncate hidden md:block">
          {rule.unavailable_reason}
        </span>
        <span className="text-[10px] text-gray-400 w-16 text-right">{rule.default_severity}</span>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-muted/30 transition-colors -mx-1 px-1 rounded">
        <Switch
          checked={isEnabled}
          onCheckedChange={(checked) => onUpdate(rule.rule_id, { enabled: checked })}
          disabled={isUpdating && pendingRuleId === rule.rule_id}
          className="scale-[0.65] origin-left"
        />
        <div className="flex-1 min-w-0">
          <span className={`text-sm ${isEnabled ? "font-medium" : "text-muted-foreground"}`}>
            {rule.name}
          </span>
          <p className="text-xs text-muted-foreground truncate">{rule.description}</p>
        </div>
        {rule.threshold != null && !editing && (
          <span className="text-xs text-muted-foreground font-mono">
            {rule.threshold_label?.includes("Z-score") ? `z>${rule.threshold}` : `<${rule.threshold}`}
          </span>
        )}
        <Badge className={`text-[10px] px-1.5 py-0 h-5 ${SEVERITY_COLORS[rule.current_severity]}`}>
          {rule.current_severity}
        </Badge>
        <button
          onClick={() => setEditing(!editing)}
          className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
      {editing && (
        <div className="flex items-center gap-2 py-2 pl-10 border-b border-gray-100 bg-muted/20 -mx-1 px-1 rounded-b">
          {rule.threshold_label && (
            <>
              <span className="text-xs text-muted-foreground">{rule.threshold_label}:</span>
              <Input
                type="number"
                step="0.1"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                className="h-7 w-20 text-xs"
              />
            </>
          )}
          <span className="text-xs text-muted-foreground">Severity:</span>
          <Select value={severity} onValueChange={(v) => setSeverity(v as DriftSeverity)}>
            <SelectTrigger className="h-8 w-[100px] rounded-full bg-white text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-[20px] bg-white">
              <SelectItem value="CRITICAL">Critical</SelectItem>
              <SelectItem value="HIGH">High</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="LOW">Low</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={handleSave} disabled={isUpdating}>
            Save
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      )}
    </>
  );
}

// ── Drift Events Table ───────────────────────────────────────────────
// Matches the IssuesTab table pattern exactly

// ── Main Component ───────────────────────────────────────────────────

export function DriftTab({ agentId }: DriftTabProps) {
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>("all");

  const { data: baseline, isLoading: baselineLoading } = useAgentBaseline(agentId);
  const { data: events, isLoading: eventsLoading } = useAgentDriftEvents(agentId, {
    severity: severityFilter !== "all" ? severityFilter : undefined,
    drift_type: typeFilter !== "all" ? typeFilter : undefined,
  });
  const { data: rules, isLoading: rulesLoading } = useAgentRules(agentId);

  const updateStatus = useUpdateDriftEventStatus();
  const updateRule = useUpdateAgentRule(agentId);
  const recompute = useRecomputeBaseline();

  const healthScore = useMemo(() => computeHealthScore(events), [events]);
  const openCount = useMemo(
    () => events?.filter((e) => e.status === "OPEN").length ?? 0,
    [events],
  );

  const driftTypes = useMemo(() => {
    if (!events) return [];
    return [...new Set(events.map((e) => e.drift_type))];
  }, [events]);

  const activeRules = useMemo(() => rules?.filter((r) => r.available) ?? [], [rules]);
  const unavailableRules = useMemo(() => rules?.filter((r) => !r.available) ?? [], [rules]);

  const isLoading = baselineLoading || eventsLoading;

  return (
    <div className="space-y-6 pt-6">
      {/* Filters */}
      <div className="flex justify-end gap-2">
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="h-9 w-[130px] rounded-full bg-white text-xs">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent className="rounded-[20px] bg-white">
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="CRITICAL">Critical</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 w-[140px] rounded-full bg-white text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="rounded-[20px] bg-white">
            <SelectItem value="all">All Types</SelectItem>
            {driftTypes.map((t) => (
              <SelectItem key={t} value={t}>
                {DRIFT_TYPE_LABELS[t] || t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary strip */}
      <Card className="rounded-[15px] shadow-none">
        <CardContent className="py-4">
          {isLoading ? (
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 divide-x divide-border">
              <div className="pl-4 first:pl-0">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Health Score</div>
                <div className="text-2xl font-bold mt-0.5">{healthScore}</div>
                <div className="text-xs text-muted-foreground">{healthLabel(healthScore)}</div>
              </div>
              <div className="pl-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Open Events</div>
                <div className="text-2xl font-bold mt-0.5">{openCount}</div>
                <div className="text-xs text-muted-foreground">{events?.length ?? 0} total</div>
              </div>
              <div className="pl-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Baseline</div>
                <div className="text-2xl font-bold mt-0.5">
                  {baseline ? baseline.status.replace("_", " ") : "--"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {baseline ? `${baseline.sample_count.toLocaleString()} samples` : "Not computed"}
                </div>
              </div>
              <div className="pl-4">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Active Rules</div>
                <div className="text-2xl font-bold mt-0.5">
                  {rules ? `${activeRules.filter((r) => r.enabled).length}` : "--"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {rules ? `of ${rules.length} total` : "Loading"}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guidance banner */}
      {!isLoading && (
        <BaselineGuidance
          baseline={baseline}
          onCompute={() => recompute.mutate(agentId)}
          isComputing={recompute.isPending}
        />
      )}

      {/* Rules section */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Detection Rules
            {rules && (
              <span className="text-muted-foreground font-normal text-sm">
                ({activeRules.filter((r) => r.enabled).length} of {rules.length} active)
              </span>
            )}
          </CardTitle>
          {baseline && baseline.status === "ACTIVE" && baseline.sample_count >= MIN_RELIABLE_SAMPLES && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full text-xs"
              onClick={() => recompute.mutate(agentId)}
              disabled={recompute.isPending}
            >
              <RefreshCw className={`h-3 w-3 mr-1.5 ${recompute.isPending ? "animate-spin" : ""}`} />
              Recompute
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-4">
            Each rule monitors a specific behavior. The severity badge shows how the alert is classified when triggered.
            Disable rules you don't need, or click the pencil to adjust sensitivity.
          </p>
          {rulesLoading && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
          {!rulesLoading && rules && (
            <div className="space-y-1.5">
              {activeRules.map((rule) => (
                <RuleRow
                  key={rule.rule_id}
                  rule={rule}
                  onUpdate={(ruleId, update) => updateRule.mutate({ ruleId, update })}
                  isUpdating={updateRule.isPending}
                  pendingRuleId={updateRule.variables?.ruleId}
                  pendingEnabled={updateRule.variables?.update.enabled}
                />
              ))}
              {unavailableRules.length > 0 && (
                <>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider pt-3 pb-1">
                    Unavailable
                  </div>
                  {unavailableRules.map((rule) => (
                    <RuleRow
                      key={rule.rule_id}
                      rule={rule}
                      onUpdate={(ruleId, update) => updateRule.mutate({ ruleId, update })}
                      isUpdating={updateRule.isPending}
                      pendingRuleId={updateRule.variables?.ruleId}
                      pendingEnabled={updateRule.variables?.update.enabled}
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drift Events — table with expandable detail rows */}
      {!isLoading && events && events.length > 0 && (
        <div>
          <h3 className="text-base font-medium flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4" />
            Drift Events
            <span className="text-muted-foreground font-normal text-sm">({events.length})</span>
          </h3>
          <div className="overflow-hidden rounded-[15px] border bg-white">
            <Table>
              <TableHeader>
                <TableRow className="h-9">
                  <TableHead className="py-2 text-xs font-medium">Title</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[100px]">Severity</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[100px]">Status</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[140px]">Detected</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[160px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => {
                  const statusConfig = STATUS_STYLES[event.status];
                  const StatusIcon = statusConfig.icon;
                  const isExpanded = expandedEventId === event.id;
                  return (
                    <>
                      <TableRow
                        key={event.id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => setExpandedEventId(isExpanded ? null : event.id)}
                        tabIndex={0}
                        onKeyDown={(keyEvent) => {
                          if (keyEvent.key === "Enter" || keyEvent.key === " ") setExpandedEventId(isExpanded ? null : event.id);
                        }}
                      >
                        <TableCell className="py-3">
                          <span className="text-sm font-medium">{event.title}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {DRIFT_TYPE_LABELS[event.drift_type] || event.drift_type}
                            </span>
                            {event.trace_ids.length > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {event.trace_ids.length} trace{event.trace_ids.length > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-3">
                          <Badge className={`rounded-[5px] text-xs ${SEVERITY_COLORS[event.severity]}`}>
                            {event.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-3">
                          <Badge variant="outline" className={`rounded-[5px] text-xs ${statusConfig.className}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {event.status.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-3">
                          <span className="text-xs text-muted-foreground">
                            {safeFormatDistanceToNow(event.detected_at, { addSuffix: true })}
                          </span>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          {event.status === "OPEN" && (
                            <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => updateStatus.mutate({ eventId: event.id, status: "ACKNOWLEDGED" })} disabled={updateStatus.isPending}>
                                Ack
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => updateStatus.mutate({ eventId: event.id, status: "FALSE_POSITIVE" })} disabled={updateStatus.isPending}>
                                FP
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => updateStatus.mutate({ eventId: event.id, status: "RESOLVED" })} disabled={updateStatus.isPending}>
                                Resolve
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Expandable detail — left accent bar, indented, clean */}
                      {isExpanded && (
                        <TableRow key={`${event.id}-detail`}>
                          <TableCell colSpan={5} className="p-0 border-b">
                            <div className={`flex border-l-[3px] ml-4 mr-4 my-2 ${
                              event.severity === "CRITICAL" ? "border-l-red-500" :
                              event.severity === "HIGH" ? "border-l-orange-500" :
                              "border-l-yellow-500"
                            }`}>
                              <div className="pl-4 py-2.5 space-y-2.5 max-w-2xl">
                                {/* Description */}
                                <p className="text-[13px] text-foreground/70 leading-relaxed">
                                  {event.description}
                                </p>

                                {/* Comparison — clean aligned rows */}
                                <table className="text-xs">
                                  <tbody>
                                    {Object.keys(event.baseline_value).map((key) => {
                                      const base = event.baseline_value[key];
                                      const obs = event.observed_value[key];
                                      const isHash = typeof base === "string" && base.length > 20;
                                      const numBase = typeof base === "number" ? base : null;
                                      const numObs = typeof obs === "number" ? obs : null;
                                      const delta = numBase && numObs && numBase > 0
                                        ? Math.round(((numObs - numBase) / numBase) * 100) : null;

                                      // Format array values as readable lists
                                      const fmtVal = (v: unknown): string => {
                                        if (Array.isArray(v)) return v.join(", ");
                                        if (typeof v === "number") return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
                                        return String(v ?? "—");
                                      };

                                      return (
                                        <tr key={key}>
                                          <td className="text-muted-foreground pr-6 py-[3px] align-top whitespace-nowrap">
                                            {key.replace(/_/g, " ")}
                                          </td>
                                          <td className="py-[3px]">
                                            {isHash ? (
                                              <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px] px-1.5 py-0 font-medium">
                                                Modified
                                              </Badge>
                                            ) : numBase !== null && numObs !== null ? (
                                              <span className="inline-flex items-center gap-1.5">
                                                <span className="font-mono text-muted-foreground">{fmtVal(base)}</span>
                                                <span className="text-muted-foreground/50">→</span>
                                                <span className="font-mono font-medium">{fmtVal(obs)}</span>
                                                {delta !== null && delta !== 0 && (
                                                  <Badge variant="outline" className={`text-[10px] px-1 py-0 font-mono ${
                                                    delta > 0 ? "text-red-600 border-red-200" : "text-green-600 border-green-200"
                                                  }`}>
                                                    {delta > 0 ? "+" : ""}{delta}%
                                                  </Badge>
                                                )}
                                              </span>
                                            ) : obs !== undefined && String(obs) !== String(base) ? (
                                              <span className="inline-flex items-center gap-1.5">
                                                <span className="font-mono text-muted-foreground">{fmtVal(base)}</span>
                                                <span className="text-muted-foreground/50">→</span>
                                                <span className="font-mono font-medium">{fmtVal(obs)}</span>
                                              </span>
                                            ) : (
                                              <span className="font-mono text-muted-foreground">{fmtVal(base)}</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>

                                {/* Footer */}
                                <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-0.5">
                                  <span>
                                    Confidence:{" "}
                                    <span className={`font-medium ${
                                      event.deviation_score >= 0.8 ? "text-red-600" :
                                      event.deviation_score >= 0.5 ? "text-orange-500" :
                                      "text-yellow-600"
                                    }`}>
                                      {event.deviation_score >= 0.8 ? "High" :
                                       event.deviation_score >= 0.5 ? "Medium" : "Low"}
                                    </span>
                                  </span>
                                  {event.trace_ids.length > 0 && (
                                    <span>{event.trace_ids.length} trace{event.trace_ids.length !== 1 ? "s" : ""}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Empty state — minimal when no events */}
      {!isLoading && (!events || events.length === 0) && baseline && baseline.status === "ACTIVE" && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <ShieldCheck className="h-4 w-4 opacity-50" />
          No drift events — agent behavior is within expected parameters
        </div>
      )}

      {/* Baseline Metrics — compact reference strip, not KPI cards */}
      {!isLoading && baseline && baseline.status === "ACTIVE" && (
        <Card className="rounded-[15px] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Baseline Reference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-x-6 gap-y-3">
              {[
                { label: "Input Tokens", value: baseline.metrics.avg_input_tokens.toFixed(0) },
                { label: "Output Tokens", value: baseline.metrics.avg_output_tokens.toFixed(0) },
                { label: "Risk Score", value: baseline.metrics.avg_risk_score.toFixed(3) },
                { label: "Requests/Hr", value: baseline.metrics.avg_requests_per_hour.toFixed(1) },
                { label: "Model", value: baseline.metrics.model_name || "--" },
                {
                  label: "Active Hours",
                  value: baseline.metrics.active_hours.length > 0
                    ? `${baseline.metrics.active_hours[0]}–${baseline.metrics.active_hours[baseline.metrics.active_hours.length - 1]} UTC`
                    : "--",
                },
              ].map((item) => (
                <div key={item.label}>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{item.label}</div>
                  <div className="text-sm font-semibold mt-0.5">{item.value}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
